import { Space, Spin, TableProps, Tooltip, Typography } from "antd";
import { EyeOutlined, DownloadOutlined, MailOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { IInvoiceList } from "./types";
import { getFlagFromAlpha3 } from "../../../utils/getFlags";

const { Text } = Typography;

type ColumnProps = {
	onView: (record: IInvoiceList) => void;
	onReject: (record: IInvoiceList) => void;
	loadingRows: Record<number, boolean>;
	loadingRowsDownload: Record<number, boolean>;
	loadingReminderRows: Record<number, boolean>;
	onDownload: (record: IInvoiceList) => void;
	navigate: (path: string) => void;
	onSendReminder: (invoiceId: number) => void;
};

export const getColumns = ({
	onView,
	onReject,
	loadingRows,
	loadingRowsDownload,
	loadingReminderRows,
	onDownload,
	navigate,
	onSendReminder
}: ColumnProps): TableProps<IInvoiceList>["columns"] => [
	{
		title: "From",
		dataIndex: "from",
		key: "from",
		sorter: (a, b) => a.from.localeCompare(b.from)
	},
	{
		title: "To",
		dataIndex: "to",
		key: "to",
		sorter: (a, b) => a.to.localeCompare(b.to)
	},
	{
		title: "Payment Id",
		dataIndex: "paymentId",
		key: "paymentId",
		align: "left",
		sorter: (a, b) => {
			const p1 = a.paymentId ?? "";
			const p2 = b.paymentId ?? "";
			return p1.toString().localeCompare(p2.toString());
		},
		render: (text) => (
			<Text
				onClick={() => {
					navigate(`/admin/transaction-details/${text}/89999`);
				}}
				style={{ color: "var(--purple-light)", fontSize: "13px", cursor: "pointer" }}
			>
				{text}
			</Text>
		)
	},
	{
		title: "Payment Type",
		dataIndex: "paymentType",
		key: "paymentType",
		align: "left",
		render: (text) => (
			<span
				style={{
					color: "var(--purple-dark)",
					fontSize: "13px"
				}}
			>
				{text}
			</span>
		)
	},
	{
		title: "Invoice Number",
		dataIndex: "invoiceNumber",
		key: "invoiceNumber",
		sorter: (a, b) => a.invoiceNumber.localeCompare(b.invoiceNumber)
	},
	{
		title: "Invoice Date",
		dataIndex: "invoiceCreatedDate",
		key: "invoiceCreatedDate",
		sorter: (a, b) => new Date(a.invoiceCreatedDate).getTime() - new Date(b.invoiceCreatedDate).getTime(),
		render: (date: string) =>
			new Date(date).toLocaleDateString("en-GB", {
				day: "2-digit",
				month: "short",
				year: "numeric"
			})
	},
	{
		title: "Due Date",
		dataIndex: "dueDate",
		key: "dueDate",
		sorter: (a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime(),
		render: (date: string) =>
			new Date(date).toLocaleDateString("en-GB", {
				day: "2-digit",
				month: "short",
				year: "numeric"
			})
	},
	{
		title: "Send Country / Currency",
		dataIndex: "sendCurrency",
		key: "sendCurrency",
		align: "left",
		render: (text, record) => (
			<div
				style={{
					color: "var(--purple-light)",
					fontSize: "13px"
				}}
			>
				<span style={{ display: "flex", alignItems: "center", marginRight: "4px" }}>
					<img
						src={getFlagFromAlpha3("USA")}
						alt={"USA"}
						style={{
							width: "15px",
							height: "15px",
							borderRadius: "50%",
							marginRight: "4px",
							objectFit: "cover"
						}}
					/>
					{`USA / USD`}
				</span>
			</div>
		)
	},
	{
		title: "Send Amount",
		dataIndex: "sendAmount",
		key: "sendAmount",
		sorter: (a, b) => (a.sendAmount ?? 0) - (b.sendAmount ?? 0),
		render: (amount, record) =>
			amount != null && record.sendCurrencyCode
				? `${record.sendCurrencyCode} ${amount.toLocaleString(undefined, {
						minimumFractionDigits: 2,
						maximumFractionDigits: 2
					})}`
				: "-"
	},
	{
		title: "Receive Country / Currency",
		dataIndex: "receiveCurrency",
		key: "receiveCurrency",
		align: "left",
		render: (text, record) => (
			<div
				style={{
					color: "var(--purple-light)",
					fontSize: "13px"
				}}
			>
				{record.receiveCountryCode && (
					<span style={{ display: "flex", alignItems: "center", marginRight: "4px" }}>
						<img
							src={getFlagFromAlpha3(record.receiveCountryCode)}
							alt={record.receiveCountryCode}
							style={{
								width: "15px",
								height: "15px",
								borderRadius: "50%",
								marginRight: "4px",
								objectFit: "cover"
							}}
						/>
						{`${record.receiveCountryCode} /`}
						{text}
					</span>
				)}
			</div>
		)
	},
	{
		title: "Receive Amount",
		dataIndex: "receiveAmount",
		key: "receiveAmount",
		sorter: (a, b) => (a.receiveAmount ?? 0) - (b.receiveAmount ?? 0)
	},
	{
		title: "Status",
		dataIndex: "statusName",
		key: "statusName",
		sorter: (a, b) => a.statusName.localeCompare(b.statusName)
	},
	{
		title: "Last Updated",
		dataIndex: "invoiceModifiedDate",
		key: "invoiceModifiedDate",
		sorter: (a, b) => new Date(a.invoiceModifiedDate).getTime() - new Date(b.invoiceModifiedDate).getTime(),
		render: (date: string) =>
			new Date(date).toLocaleDateString("en-GB", {
				day: "2-digit",
				month: "short",
				year: "numeric"
			})
	},
	{
		title: "Action",
		key: "action",
		fixed: "right",
		render: (_, record) => {
			const isSent = record.statusName?.toLowerCase() === "sent";

			return (
				<Space size="middle">
					<Tooltip title="View Invoice">
						<a
							onClick={(e) => {
								e.stopPropagation();
								if (!loadingRows[record.invoiceId]) onView(record);
							}}
							style={{
								pointerEvents: loadingRows[record.invoiceId] ? "none" : "auto"
							}}
						>
							{loadingRows[record.invoiceId] ? <Spin size="small" /> : <EyeOutlined />}
						</a>
					</Tooltip>
					<Tooltip title="Download Invoice">
						<a
							onClick={(e) => {
								e.stopPropagation();
								if (!loadingRowsDownload[record.invoiceId]) onDownload(record);
							}}
							style={{
								pointerEvents: loadingRowsDownload[record.invoiceId] ? "none" : "auto"
							}}
						>
							{loadingRowsDownload[record.invoiceId] ? (
								<Spin size="small" />
							) : (
								<DownloadOutlined
									style={{
										fontSize: "24px",
										color: "var(--purple-dark)",
										cursor: "pointer"
									}}
								/>
							)}
						</a>
					</Tooltip>
					<Tooltip title="Discard Invoice">
						<a
							onClick={(e) => {
								e.stopPropagation();
								if (record.statusName !== "Sent") return;
								onReject(record);
							}}
							style={{
								color: "oklch(63.7% 0.237 25.331)",
								opacity: record.statusName === "Sent" ? 1 : 0.4,
								pointerEvents: record.statusName === "Sent" ? "auto" : "none",
								cursor: record.statusName === "Sent" ? "pointer" : "not-allowed"
							}}
						>
							<CloseCircleOutlined
								style={{
									fontSize: "24px",
									color: "oklch(63.7% 0.237 25.331)"
								}}
							/>
						</a>
					</Tooltip>

					<Tooltip title={isSent ? "Send Reminder Mail" : "Reminder available only for Status Sent invoices"}>
						<a
							onClick={(e) => {
								e.stopPropagation();

								if (isSent) {
									onSendReminder(record.invoiceId);
								}
							}}
							style={{
								pointerEvents: isSent ? "auto" : "none",
								opacity: isSent ? 1 : 0.4
							}}
						>
							{loadingReminderRows[record.invoiceId] ? (
								<Spin size="small" />
							) : (
								<MailOutlined
									style={{
										fontSize: "22px",
										color: isSent ? "#fa8c16" : "#bfbfbf",
										cursor: isSent ? "pointer" : "not-allowed"
									}}
								/>
							)}
						</a>
					</Tooltip>
				</Space>
			);
		}
	}
];
